import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Progress } from 'components/ui/progress';
import { cn } from 'lib/utils';

interface AppLoadingProps {
  message?: string;
  progress?: number;
  isExiting?: boolean;
  variant?: 'fullscreen' | 'overlay';
}

export const AppLoading = ({
  message,
  progress = 0,
  isExiting = false,
  variant = 'fullscreen',
}: AppLoadingProps) => {
  const { t } = useTranslation('common');

  // Step calculation logic
  const getCurrentStep = () => {
    if (progress < 25) return t('appLoading.initializing');
    if (progress < 50) return t('appLoading.loadingUserData');
    if (progress < 75) return t('appLoading.configuringMenu');
    if (progress < 100) return t('appLoading.finalizingSetup');
    return t('appLoading.applicationReady');
  };

  // If message is provided, use it. Otherwise use default loading message when progress is 0
  // or show step message when progress > 0
  const stepMessage = message || (progress > 0 ? getCurrentStep() : t('appLoading.loadingApplication'));

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.4,
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.4 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -20, scale: 0.8 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.3 },
    },
  };

  return (
    <AnimatePresence>
      {!isExiting && (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={cn(
            'flex flex-col items-center justify-center gap-6 bg-background',
            variant === 'fullscreen' ? 'fixed inset-0 z-[9999]' : 'absolute z-[1000]'
          )}
          role="status"
          aria-busy="true"
          aria-live="polite"
          data-exit={isExiting}
        >
          {/* Animated Logo */}
          <motion.div variants={itemVariants}>
            <Loader2 className="h-16 w-16 animate-spin text-primary" aria-hidden="true" />
          </motion.div>

          {/* Message */}
          <motion.p variants={itemVariants} className="text-lg font-medium text-foreground">
            {stepMessage}
            <span className="sr-only">{stepMessage}</span>
          </motion.p>

          {/* Progress Bar */}
          {progress > 0 && (
            <motion.div variants={itemVariants} className="w-64">
              <Progress
                value={progress}
                className="h-2"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
                aria-label={`Progress: ${progress}%`}
              />
              <p className="mt-2 text-center text-sm text-muted-foreground">{progress}%</p>
            </motion.div>
          )}

          {/* Bouncing Dots */}
          <motion.div variants={itemVariants} className="flex gap-2">
            {[0, 1, 2].map(index => (
              <motion.div
                key={index}
                className="h-2 w-2 rounded-full bg-primary"
                data-testid="loading-dot"
                animate={{
                  y: ['0%', '-50%', '0%'],
                }}
                transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  delay: index * 0.15,
                }}
              />
            ))}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
